import { useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";

import { cn } from "~/lib/utils";
import { type AxisAngleRotation } from "./accelerometerOrientation.utils";

interface OrientationCubeProps {
  rotation: AxisAngleRotation;
  className?: string;
  size?: number;
}

type SceneHandles = {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  modelGroup: THREE.Group;
  controls: OrbitControls;
};

const STAGE_WIDTH_SCALE = 3.1;
const STAGE_HEIGHT_SCALE = 2.15;

const BODY_WIDTH = 1.9;
const BODY_HEIGHT = 1.02;
const BODY_DEPTH = 0.92;
const BODY_RADIUS = 0.1;

const GROUND_Y = -(BODY_HEIGHT / 2) - 0.16;

const cssColorVar = (
  styles: CSSStyleDeclaration,
  variableName: string,
  fallback: string
) => styles.getPropertyValue(variableName).trim() || fallback;

const roundedBox = (
  width: number,
  height: number,
  depth: number,
  radius: number,
  segments: number = 4
) => new RoundedBoxGeometry(width, height, depth, segments, radius);

const createLabelTexture = ({
  label,
  color,
}: {
  label: string;
  color: string;
}) => {
  const canvas = document.createElement("canvas");
  canvas.width = 256;
  canvas.height = 128;

  const context = canvas.getContext("2d");
  if (!context) {
    return null;
  }

  context.clearRect(0, 0, canvas.width, canvas.height);
  context.fillStyle = color;
  context.font = "600 36px 'IBM Plex Mono', monospace";
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.fillText(label, canvas.width / 2, canvas.height / 2);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.needsUpdate = true;
  return texture;
};

const addScrew = (
  parent: THREE.Group,
  position: THREE.Vector3,
  material: THREE.Material
) => {
  const mesh = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.04, 0.02, 20), material);
  mesh.rotation.x = Math.PI / 2;
  mesh.position.copy(position);
  mesh.name = "device-screw";
  parent.add(mesh);
};

const addLabelPlane = ({
  parent,
  label,
  color,
  width,
  height,
  position,
  rotation,
}: {
  parent: THREE.Group;
  label: string;
  color: string;
  width: number;
  height: number;
  position: THREE.Vector3;
  rotation: THREE.Euler;
}) => {
  const texture = createLabelTexture({ label, color });
  const material = new THREE.MeshBasicMaterial({
    map: texture,
    transparent: true,
    depthWrite: false,
  });
  const plane = new THREE.Mesh(new THREE.PlaneGeometry(width, height), material);
  plane.position.copy(position);
  plane.rotation.copy(rotation);
  plane.name = `device-label-${label.toLowerCase()}`;
  parent.add(plane);
};

const buildDeviceModel = () => {
  const modelGroup = new THREE.Group();

  const bodyMaterial = new THREE.MeshStandardMaterial({
    color: "#96e4f2",
    roughness: 0.78,
    metalness: 0.04,
  });
  const panelMaterial = new THREE.MeshStandardMaterial({
    color: "#1a2d5c",
    roughness: 0.62,
    metalness: 0.12,
  });
  const hardwareMaterial = new THREE.MeshStandardMaterial({
    color: "#d5d8dc",
    roughness: 0.35,
    metalness: 0.85,
  });
  const trimMaterial = new THREE.MeshStandardMaterial({
    color: "#162452",
    roughness: 0.5,
    metalness: 0.14,
  });
  const voidMaterial = new THREE.MeshStandardMaterial({
    color: "#0e1a3e",
    roughness: 1,
    metalness: 0,
  });
  const ledMaterial = new THREE.MeshStandardMaterial({
    color: "#84f45d",
    emissive: "#84f45d",
    emissiveIntensity: 1.35,
    roughness: 0.35,
    metalness: 0.1,
  });

  const body = new THREE.Mesh(
    roundedBox(BODY_WIDTH, BODY_HEIGHT, BODY_DEPTH, BODY_RADIUS, 6),
    bodyMaterial
  );
  body.name = "device-body";
  body.position.y = 0.04;
  modelGroup.add(body);

  const frontPanel = new THREE.Mesh(
    roundedBox(1.58, 0.98, 0.06, 0.045, 4),
    panelMaterial
  );
  frontPanel.position.set(0, 0.06, BODY_DEPTH / 2 - 0.015);
  frontPanel.name = "device-front-panel";
  modelGroup.add(frontPanel);

  const serviceDoor = new THREE.Mesh(
    roundedBox(0.48, 0.28, 0.045, 0.035, 4),
    panelMaterial.clone()
  );
  serviceDoor.position.set(0, -0.11, BODY_DEPTH / 2 + 0.02);
  serviceDoor.name = "device-service-door";
  modelGroup.add(serviceDoor);

  const latchBase = new THREE.Mesh(
    new THREE.CylinderGeometry(0.11, 0.11, 0.05, 28),
    hardwareMaterial
  );
  latchBase.rotation.x = Math.PI / 2;
  latchBase.position.set(0, -0.11, BODY_DEPTH / 2 + 0.055);
  latchBase.name = "device-latch-base";
  modelGroup.add(latchBase);

  const latchHandle = new THREE.Mesh(
    roundedBox(0.16, 0.05, 0.06, 0.015, 3),
    hardwareMaterial
  );
  latchHandle.position.set(0.04, -0.11, BODY_DEPTH / 2 + 0.11);
  latchHandle.rotation.z = 0.18;
  latchHandle.name = "device-latch-handle";
  modelGroup.add(latchHandle);

  // Handle — single extruded arch with angled pillars
  const bevelSize = 0.05;
  const handleHeight = 0.38;
  const handleBaseY = 0.46;
  const handleBottomOuter = BODY_WIDTH - bevelSize * 2 - 0.06;
  const handleTopOuter = 1.25;
  const handlePillarThick = 0.20;
  const handleBarThick = 0.12;
  const handleBaseDepth = BODY_DEPTH - bevelSize * 2;
  const handleGripDepth = 0.35;
  const handleZ = 0;

  const handleShape = new THREE.Shape();
  handleShape.moveTo(-handleBottomOuter / 2, 0);
  handleShape.lineTo(handleBottomOuter / 2, 0);
  handleShape.lineTo(handleTopOuter / 2, handleHeight);
  handleShape.lineTo(-handleTopOuter / 2, handleHeight);
  handleShape.closePath();

  const innerBottomW = handleBottomOuter - 2 * handlePillarThick;
  const innerTopW = handleTopOuter - 2 * handlePillarThick;
  const innerTop = handleHeight - handleBarThick;

  const hole = new THREE.Path();
  hole.moveTo(-innerBottomW / 2, 0.005);
  hole.lineTo(innerBottomW / 2, 0.005);
  hole.lineTo(innerTopW / 2, innerTop);
  hole.lineTo(-innerTopW / 2, innerTop);
  hole.closePath();
  handleShape.holes.push(hole);

  const handleGeo = new THREE.ExtrudeGeometry(handleShape, {
    depth: handleBaseDepth,
    bevelEnabled: true,
    bevelThickness: bevelSize,
    bevelSize,
    bevelSegments: 4,
  });
  handleGeo.translate(0, 0, -handleBaseDepth / 2);

  // Taper depth: body-deep at base, narrow grip at top
  const posAttr = handleGeo.getAttribute("position");
  for (let i = 0; i < posAttr.count; i++) {
    const y = posAttr.getY(i);
    const z = posAttr.getZ(i);
    const t = Math.max(0, Math.min(1, y / handleHeight));
    const scale = 1 - t * (1 - handleGripDepth / handleBaseDepth);
    posAttr.setZ(i, z * scale);
  }
  posAttr.needsUpdate = true;
  handleGeo.computeVertexNormals();

  const handleMesh = new THREE.Mesh(handleGeo, bodyMaterial);
  handleMesh.position.set(0, handleBaseY + bevelSize, handleZ);
  handleMesh.name = "device-handle-shell";
  modelGroup.add(handleMesh);

  // Dark navy front face on handle pillars (stops at grip opening top)
  const panelTopY = innerTop + 0.01;
  const panelTopOuterW =
    handleBottomOuter +
    (handleTopOuter - handleBottomOuter) * (panelTopY / handleHeight);

  const handleFrontShape = new THREE.Shape();
  handleFrontShape.moveTo(-handleBottomOuter / 2, 0);
  handleFrontShape.lineTo(handleBottomOuter / 2, 0);
  handleFrontShape.lineTo(panelTopOuterW / 2, panelTopY);
  handleFrontShape.lineTo(-panelTopOuterW / 2, panelTopY);
  handleFrontShape.closePath();

  const panelHole = new THREE.Path();
  panelHole.moveTo(-innerBottomW / 2, 0.005);
  panelHole.lineTo(innerBottomW / 2, 0.005);
  panelHole.lineTo(innerTopW / 2, innerTop);
  panelHole.lineTo(-innerTopW / 2, innerTop);
  panelHole.closePath();
  handleFrontShape.holes.push(panelHole);

  const handleFrontGeo = new THREE.ShapeGeometry(handleFrontShape);
  const frontPosAttr = handleFrontGeo.getAttribute("position");
  for (let i = 0; i < frontPosAttr.count; i++) {
    const y = frontPosAttr.getY(i);
    const t = Math.max(0, Math.min(1, y / handleHeight));
    const scale = 1 - t * (1 - handleGripDepth / handleBaseDepth);
    frontPosAttr.setZ(i, (handleBaseDepth / 2 + bevelSize) * scale + 0.005);
  }
  frontPosAttr.needsUpdate = true;
  handleFrontGeo.computeVertexNormals();

  const handleFrontPanel = new THREE.Mesh(handleFrontGeo, panelMaterial.clone());
  handleFrontPanel.position.set(0, handleBaseY + bevelSize, handleZ);
  handleFrontPanel.name = "device-front-panel";
  modelGroup.add(handleFrontPanel);

  // LED positioning on left pillar
  const ledY = 0.67;
  const ledShapeY = ledY - (handleBaseY + bevelSize);
  const ledT = Math.max(0, Math.min(1, ledShapeY / handleHeight));
  const ledDepthScale = 1 - ledT * (1 - handleGripDepth / handleBaseDepth);
  const pillarFrontZ =
    handleZ + (handleBaseDepth / 2 + bevelSize) * ledDepthScale;
  const leftPillarX = -0.66;
  const ledAngle = -0.44; // ~25 degrees tilted right

  const sideRecess = new THREE.Mesh(
    roundedBox(0.38, 0.46, 0.16, 0.03, 4),
    voidMaterial
  );
  sideRecess.position.set(-(BODY_WIDTH / 2) + 0.05, 0.02, -0.12);
  sideRecess.rotation.y = Math.PI / 2;
  sideRecess.name = "device-side-recess";
  modelGroup.add(sideRecess);

  const sideSocket = new THREE.Mesh(
    new THREE.CylinderGeometry(0.1, 0.1, 0.09, 28),
    trimMaterial
  );
  sideSocket.rotation.y = Math.PI / 2;
  sideSocket.position.set(-(BODY_WIDTH / 2) - 0.005, -0.28, -0.12);
  sideSocket.name = "device-side-socket";
  modelGroup.add(sideSocket);

  const sideSocketHandle = new THREE.Mesh(
    roundedBox(0.12, 0.06, 0.03, 0.01, 2),
    hardwareMaterial
  );
  sideSocketHandle.position.set(-(BODY_WIDTH / 2) - 0.04, -0.28, -0.08);
  sideSocketHandle.rotation.y = Math.PI / 2;
  modelGroup.add(sideSocketHandle);

  [-0.05, 0, 0.05].forEach((offset) => {
    const led = new THREE.Mesh(
      roundedBox(0.03, 0.105, 0.014, 0.006, 2),
      ledMaterial
    );
    led.position.set(leftPillarX + offset, ledY, pillarFrontZ + 0.04);
    led.rotation.z = ledAngle;
    led.name = "device-led";
    modelGroup.add(led);
  });

  [
    new THREE.Vector3(-0.56, 0.3, BODY_DEPTH / 2 + 0.03),
    new THREE.Vector3(-0.18, 0.31, BODY_DEPTH / 2 + 0.03),
    new THREE.Vector3(0.18, 0.31, BODY_DEPTH / 2 + 0.03),
    new THREE.Vector3(0.56, 0.3, BODY_DEPTH / 2 + 0.03),
    new THREE.Vector3(-0.6, -0.02, BODY_DEPTH / 2 + 0.03),
    new THREE.Vector3(0.6, -0.02, BODY_DEPTH / 2 + 0.03),
    new THREE.Vector3(-0.56, -0.34, BODY_DEPTH / 2 + 0.03),
    new THREE.Vector3(0.56, -0.34, BODY_DEPTH / 2 + 0.03),
    new THREE.Vector3(-0.16, -0.11, BODY_DEPTH / 2 + 0.065),
    new THREE.Vector3(0.16, -0.11, BODY_DEPTH / 2 + 0.065),
  ].forEach((position) => addScrew(modelGroup, position, hardwareMaterial));

  [
    new THREE.Vector3(-0.55, GROUND_Y + 0.055, 0.24),
    new THREE.Vector3(0.55, GROUND_Y + 0.055, 0.24),
    new THREE.Vector3(-0.52, GROUND_Y + 0.055, -0.2),
    new THREE.Vector3(0.52, GROUND_Y + 0.055, -0.2),
  ].forEach((position) => {
    const foot = new THREE.Mesh(
      new THREE.CylinderGeometry(0.045, 0.055, 0.045, 18),
      trimMaterial
    );
    foot.position.copy(position);
    foot.name = "device-foot";
    modelGroup.add(foot);
  });

  const arrowGroup = new THREE.Group();
  arrowGroup.position.set(0, 0.90, 0);
  arrowGroup.name = "device-arrow";

  const arrowStem = new THREE.Mesh(
    new THREE.CylinderGeometry(0.018, 0.018, 0.4, 18),
    hardwareMaterial.clone()
  );
  arrowStem.position.y = 0.2;
  arrowStem.name = "device-arrow-stem";
  arrowGroup.add(arrowStem);

  const arrowTip = new THREE.Mesh(
    new THREE.ConeGeometry(0.07, 0.16, 18),
    hardwareMaterial.clone()
  );
  arrowTip.position.y = 0.47;
  arrowTip.name = "device-arrow-tip";
  arrowGroup.add(arrowTip);
  modelGroup.add(arrowGroup);

  const labelColor = "#0f172a";
  addLabelPlane({
    parent: modelGroup,
    label: "FRONT",
    color: labelColor,
    width: 0.65,
    height: 0.18,
    position: new THREE.Vector3(0, -0.04, BODY_DEPTH / 2 + 0.055),
    rotation: new THREE.Euler(0, 0, 0),
  });
  addLabelPlane({
    parent: modelGroup,
    label: "LEFT",
    color: labelColor,
    width: 0.5,
    height: 0.16,
    position: new THREE.Vector3(-(BODY_WIDTH / 2) - 0.035, -0.02, 0.18),
    rotation: new THREE.Euler(0, -Math.PI / 2, 0),
  });
  addLabelPlane({
    parent: modelGroup,
    label: "TOP",
    color: labelColor,
    width: 0.42,
    height: 0.16,
    position: new THREE.Vector3(0, 0.90, 0),
    rotation: new THREE.Euler(-Math.PI / 2, 0, 0),
  });

  return modelGroup;
};

const createScene = (
  canvas: HTMLCanvasElement,
  pixelWidth: number,
  pixelHeight: number
): SceneHandles => {
  const renderer = new THREE.WebGLRenderer({
    canvas,
    alpha: true,
    antialias: true,
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(pixelWidth, pixelHeight, false);
  renderer.outputColorSpace = THREE.SRGBColorSpace;

  const scene = new THREE.Scene();

  const camera = new THREE.PerspectiveCamera(
    29,
    pixelWidth / pixelHeight,
    0.1,
    100
  );
  camera.position.set(3.2, 2.0, 2.45);
  camera.lookAt(0.05, 0.28, 0);

  const controls = new OrbitControls(camera, canvas);
  controls.target.set(0.05, 0.28, 0);
  controls.enableDamping = false;
  controls.minDistance = 2.1;
  controls.maxDistance = 6;
  controls.maxPolarAngle = Math.PI * 0.48;
  controls.update();

  scene.add(new THREE.AmbientLight(0xffffff, 1.3));

  const keyLight = new THREE.DirectionalLight(0xffffff, 1.55);
  keyLight.position.set(2.8, 3.8, 2.2);
  scene.add(keyLight);

  const fillLight = new THREE.DirectionalLight(0xffffff, 0.85);
  fillLight.position.set(-2.5, 1.2, -2.7);
  scene.add(fillLight);

  const rimLight = new THREE.DirectionalLight(0x9ad8ff, 0.55);
  rimLight.position.set(0.8, 2.2, -3.2);
  scene.add(rimLight);

  const ground = new THREE.Mesh(
    new THREE.CircleGeometry(1.7, 64),
    new THREE.MeshStandardMaterial({
      color: "#8fb6d6",
      transparent: true,
      opacity: 0.12,
      roughness: 1,
    })
  );
  ground.rotation.x = -Math.PI / 2;
  ground.position.y = GROUND_Y;
  ground.scale.set(1.2, 0.78, 1);
  ground.name = "device-ground";
  scene.add(ground);

  const modelGroup = buildDeviceModel();
  scene.add(modelGroup);

  return {
    renderer,
    scene,
    camera,
    modelGroup,
    controls,
  };
};

const applyTheme = (scene: THREE.Scene) => {
  const styles = getComputedStyle(document.documentElement);
  const colors = {
    body: document.documentElement.classList.contains("dark")
      ? "#8adaec"
      : "#96e4f2",
    panel: document.documentElement.classList.contains("dark")
      ? "#1e3266"
      : "#1a2d5c",
    hardware: document.documentElement.classList.contains("dark")
      ? "#f2f4f7"
      : "#eef2f6",
    trim: document.documentElement.classList.contains("dark")
      ? "#1a2a56"
      : "#162452",
    label: document.documentElement.classList.contains("dark")
      ? "#d8e3f5"
      : "#11213f",
    ground: cssColorVar(styles, "--primary", "oklch(0.48 0.2 260.47)"),
  };

  scene.traverse((object) => {
    if (object instanceof THREE.Mesh) {
      if (
        object.name === "device-body" ||
        object.name === "device-handle-shell"
      ) {
        const material = object.material as THREE.MeshStandardMaterial;
        material.color = new THREE.Color(colors.body);
      } else if (
        object.name === "device-front-panel" ||
        object.name === "device-service-door"
      ) {
        const material = object.material as THREE.MeshStandardMaterial;
        material.color = new THREE.Color(colors.panel);
      } else if (
        object.name === "device-latch-base" ||
        object.name === "device-latch-handle" ||
        object.name === "device-screw" ||
        object.name === "device-arrow-stem" ||
        object.name === "device-arrow-tip"
      ) {
        const material = object.material as THREE.MeshStandardMaterial;
        material.color = new THREE.Color(colors.hardware);
      } else if (
        object.name === "device-side-socket" ||
        object.name === "device-foot"
      ) {
        const material = object.material as THREE.MeshStandardMaterial;
        material.color = new THREE.Color(colors.trim);
      } else if (object.name === "device-side-recess") {
        const material = object.material as THREE.MeshStandardMaterial;
        material.color = new THREE.Color(colors.panel);
      } else if (object.name === "device-ground") {
        const material = object.material as THREE.MeshStandardMaterial;
        material.color = new THREE.Color(colors.ground);
      } else if (object.name === "device-led") {
        const material = object.material as THREE.MeshStandardMaterial;
        material.color = new THREE.Color("#7ff36a");
        material.emissive = new THREE.Color("#7ff36a");
      } else if (object.name.startsWith("device-label-")) {
        const material = object.material as THREE.MeshBasicMaterial;
        const label = object.name.replace("device-label-", "").toUpperCase();
        if (material.map) {
          material.map.dispose();
        }
        material.map = createLabelTexture({
          label,
          color: colors.label,
        });
        material.needsUpdate = true;
      }
    }
  });
};

const renderScene = ({ renderer, scene, camera }: SceneHandles) => {
  renderer.render(scene, camera);
};

const disposeScene = ({ renderer, scene, controls }: SceneHandles) => {
  scene.traverse((object) => {
    if (object instanceof THREE.Mesh) {
      object.geometry.dispose();
      if (Array.isArray(object.material)) {
        object.material.forEach((material) => {
          if ("map" in material && material.map) {
            material.map.dispose();
          }
          material.dispose();
        });
      } else {
        if ("map" in object.material && object.material.map) {
          object.material.map.dispose();
        }
        object.material.dispose();
      }
    }
  });
  controls.dispose();
  renderer.dispose();
};

export const OrientationCube = ({
  rotation,
  className,
  size = 68,
}: OrientationCubeProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const sceneRef = useRef<SceneHandles | null>(null);
  const stageWidth = useMemo(
    () => Math.round(size * STAGE_WIDTH_SCALE),
    [size]
  );
  const stageHeight = useMemo(
    () => Math.round(size * STAGE_HEIGHT_SCALE),
    [size]
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const sceneHandles = createScene(canvas, stageWidth, stageHeight);
    sceneRef.current = sceneHandles;
    applyTheme(sceneHandles.scene);
    sceneHandles.controls.addEventListener("change", () =>
      renderScene(sceneHandles)
    );
    renderScene(sceneHandles);

    return () => {
      if (sceneRef.current) {
        disposeScene(sceneRef.current);
        sceneRef.current = null;
      }
    };
  }, [stageHeight, stageWidth]);

  useEffect(() => {
    const sceneHandles = sceneRef.current;
    if (!sceneHandles) {
      return;
    }

    applyTheme(sceneHandles.scene);

    const axis = new THREE.Vector3(rotation.x, rotation.y, rotation.z);
    if (axis.lengthSq() < 1e-8 || rotation.angleRad === 0) {
      sceneHandles.modelGroup.quaternion.identity();
    } else {
      axis.normalize();
      sceneHandles.modelGroup.quaternion.setFromAxisAngle(axis, rotation.angleRad);
    }

    renderScene(sceneHandles);
  }, [rotation]);

  return (
    <div
      className={cn("shrink-0", className)}
      style={{ width: stageWidth, height: stageHeight }}
    >
      <canvas
        ref={canvasRef}
        width={stageWidth}
        height={stageHeight}
        className="block h-full w-full cursor-grab active:cursor-grabbing"
        aria-label="Orientation cube"
      />
    </div>
  );
};
